// SignIn — landing page for unauthenticated visitors.
// WorkOS AuthKit handles the magic-link flow; this is the gateway page.

import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from '@avagency/frontend/components/ui/card';
import { Button } from '@avagency/frontend/components/ui/button';
import { signInURL } from '../api';

export interface SignInProps {
  returnTo?: string;
  error?: string;
}

function humanError(raw: string): string {
  if (!raw) return '';
  if (raw.toLowerCase().includes('fetch') || raw.toLowerCase().includes('network')) {
    return "We couldn't reach the server right now. Please try again in a moment.";
  }
  if (raw.toLowerCase().includes('401') || raw.toLowerCase().includes('unauthorized')) {
    return 'Your session expired. Sign in again to continue.';
  }
  return raw.replace(/^auth check failed:\s*/i, '');
}

export function SignIn({ returnTo = '/', error }: SignInProps) {
  const href = signInURL(returnTo);
  const friendlyError = error ? humanError(error) : undefined;

  return (
    <div className="pr-auth-bg flex min-h-screen items-center justify-center p-4">
      <div className="w-full max-w-md space-y-6">
        <div className="flex items-center justify-center gap-3">
          <div className="pr-brand-orb" aria-hidden="true" />
          <div>
            <div className="text-lg font-semibold tracking-tight text-foreground/95">
              Pipeline Rot Alerter
            </div>
            <div className="text-xs text-muted-foreground tracking-wide">
              CRM pipeline rot detection &amp; alerting
            </div>
          </div>
        </div>

        <Card className="pr-auth-card">
          <CardHeader className="pb-2">
            <CardTitle className="pr-page-title text-xl font-semibold">Welcome back</CardTitle>
            <CardDescription className="text-sm leading-relaxed">
              Sign in to monitor your CRM pipeline for stalled deals — configure rot thresholds,
              view the leaderboard, and receive alerts before revenue slips away.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4 pt-4">
            {friendlyError && (
              <div
                role="alert"
                className="rounded-lg border border-orange-500/20 bg-orange-500/[0.08] px-4 py-3 text-sm text-orange-300"
              >
                <p className="font-medium mb-0.5">Having trouble signing in?</p>
                <p className="text-orange-300/80 text-xs">{friendlyError}</p>
              </div>
            )}

            <Button asChild size="lg" className="w-full font-semibold shadow-lg" style={{
              boxShadow: '0 0 20px rgba(239,68,68,0.25), 0 4px 12px rgba(0,0,0,0.4)',
            }}>
              <a href={href}>Sign in with a magic link</a>
            </Button>

            <div className="space-y-1.5 text-center">
              <p className="text-xs text-muted-foreground">We email a one-time link. No password needed.</p>
              <p className="text-xs text-muted-foreground/60">Powered by WorkOS · No CRM credentials stored</p>
            </div>
          </CardContent>
        </Card>

        <p className="text-center text-xs text-muted-foreground/50">
          First time? Connect your CRM right after sign-in to start scanning for pipeline rot.
        </p>
      </div>
    </div>
  );
}
